/* eslint-disable jsx-a11y/anchor-is-valid, import/prefer-default-export */
/* eslint-disable react/jsx-filename-extension */
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import Collapse from 'react-bootstrap/Collapse';
import getUserDepartments from '../../utils/local-storage';
import { canAccessContratosAssinadosArea } from '../../utils/acl/admin-area';
import { hasPermissao } from '../../utils/acl/permissoes';
import { CONFIGURACOES_KEYS } from '../../utils/acl/configuracoes';

// Grupo do menu (classes do próprio tema SB Admin 2): divisor + título, só se a
// pessoa vê pelo menos um item do grupo (itens sem permissão vêm como false e somem).
function GrupoMenu({ titulo, children }) {
  const itens = React.Children.toArray(children);
  if (itens.length === 0) return null;
  return (
    <>
      {titulo && <hr className="sidebar-divider" />}
      {titulo && <div className="sidebar-heading">{titulo}</div>}
      {itens}
    </>
  );
}

GrupoMenu.propTypes = {
  titulo: PropTypes.string,
  children: PropTypes.node,
};

GrupoMenu.defaultProps = {
  titulo: null,
  children: null,
};

// Itens comuns a qualquer usuário logado (Tarefas de Contato/sino, Ajuda,
// Ajuda do Sistema/WhatsApp, Perfil, Sair) moraram aqui antes e agora vivem
// só na Navbar (src/components/Navbar/index.js) — não duplicar de volta pra
// cá. Esta lista fica só com os itens administrativos/restritos por setor.
export function Sidebar() {
  const { pathname } = useLocation();
  const [openLembretes, setOpenLembretes] = useState(false);
  const [openConfiguracoes, setOpenConfiguracoes] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // BottomNav (fora da Sidebar, renderizado uma vez em App.jsx) dispara esse evento pro
  // item "Configurações" abrir a gaveta completa da sidebar em vez de duplicar os ~9
  // links de configuração na barra inferior.
  useEffect(() => {
    const handler = () => setMobileMenuOpen((v) => !v);
    window.addEventListener('toggle-sidebar-mobile', handler);
    return () => window.removeEventListener('toggle-sidebar-mobile', handler);
  }, []);

  const userAccess = {
    perfilId: localStorage.getItem('perfilId'),
    isSuperAdmin: localStorage.getItem('permissoesAdmin') === 'true',
    departments: getUserDepartments(),
  };
  const canAccessContratos = canAccessContratosAssinadosArea(userAccess);
  // Usuários e Lembretes/listas moram fora e dentro de Configurações
  // respectivamente, com permissões próprias (menu.usuarios / menu.configuracoes).
  // O dropdown pai precisa abrir pra quem tem qualquer uma das duas — cada item
  // dentro continua checando sua própria permissão.
  const canAccessConfigMenu = hasPermissao('menu.usuarios')
    || CONFIGURACOES_KEYS.some((chave) => hasPermissao(chave));

  const isActive = (path) => pathname === path || pathname.startsWith(`${path}/`);
  const navItemClass = (path) => `nav-item${isActive(path) ? ' active' : ''}`;

  const handleToggleSidebar = () => {
    document.getElementById('accordionSidebar').classList.toggle('toggled');
    document.body.classList.toggle('sidebar-toggled');
  };

  return (
    <ul
      className={`navbar-nav bg-gradient-primary sidebar sidebar-dark accordion${mobileMenuOpen ? ' sidebar-mobile-expanded' : ''}`}
      id="accordionSidebar"
    >
      <Link className="sidebar-brand d-flex align-items-center justify-content-center" to="/meu-dia">
        <div className="sidebar-brand-text mx-3">Marcelo Manhães</div>
      </Link>

      {/* Sem Navbar, o mobile perdeu o hambúrguer que ficava nela. Este substitui —
          só aparece <768px (o toggle de baixo já cobre desktop). Usa estado do React
          + classe própria (não ".toggled") de propósito: um script legado do tema
          (sb-admin-2.js) adiciona ".toggled" sozinho sempre que a largura da janela
          fica <480px, e no mobile isso dispara a cada "resize" — inclusive o que o
          navegador gera ao esconder/mostrar a barra de endereço durante a rolagem.
          Usando nossa própria classe, esse script legado não tem efeito nenhum aqui. */}
      <div className="text-center d-md-none">
        <button
          className="btn btn-link text-white"
          type="button"
          onClick={() => setMobileMenuOpen((v) => !v)}
          title="Abrir/fechar menu"
          aria-label="Abrir/fechar menu"
        >
          <i className="fa fa-bars" />
        </button>
      </div>

      <hr className="sidebar-divider my-0" />

      <GrupoMenu>
        <li className={navItemClass('/meu-dia')}>
          <Link className="nav-link" to="/meu-dia">
            <i className="fas fa-fw fa-sun" />
            <span>Meu dia</span>
          </Link>
        </li>

        {hasPermissao('menu.dashboard') && (
          <li className={navItemClass('/dashboard')}>
            <Link className="nav-link" to="/dashboard">
              <i className="fas fa-fw fa-chart-line" />
              <span>Dashboard</span>
            </Link>
          </li>
        )}

        {hasPermissao('menu.pessoas') && (
          <li className={navItemClass('/pessoas')}>
            <Link className="nav-link" to="/pessoas">
              <i className="fas fa-fw fa-users" />
              <span>Cadastro Geral</span>
            </Link>
          </li>
        )}

        {hasPermissao('menu.atendimentos') && (
          <li className={navItemClass('/atendimentos-presenciais')}>
            <Link className="nav-link" to="/atendimentos-presenciais">
              <i className="fas fa-fw fa-calendar-check" />
              <span>Agenda Diária</span>
            </Link>
          </li>
        )}
      </GrupoMenu>

      <GrupoMenu titulo="Relatórios">
        {hasPermissao('relatorio.fechamentoDiario') && (
          <li className={navItemClass('/admin/relatorios/contratos-assinados')}>
            <Link className="nav-link" to="/admin/relatorios/contratos-assinados">
              <i className="fas fa-fw fa-file-contract" />
              <span>Fechamento Geral</span>
            </Link>
          </li>
        )}

        {hasPermissao('relatorio.fechamentoCallCenter') && (
          <li className={navItemClass('/admin/relatorios/fechamento-call-center')}>
            <Link className="nav-link" to="/admin/relatorios/fechamento-call-center">
              <i className="fas fa-fw fa-headset" />
              <span>Fechamento Call Center</span>
            </Link>
          </li>
        )}

        {hasPermissao('relatorio.agendamentosPorIndicacao') && (
          <li className={navItemClass('/admin/relatorios/agendamentos-por-indicacao')}>
            <Link className="nav-link" to="/admin/relatorios/agendamentos-por-indicacao">
              <i className="fas fa-fw fa-bullhorn" />
              <span>Agendamentos por Indicação</span>
            </Link>
          </li>
        )}

        {hasPermissao('relatorio.etapas') && (
          <li className={navItemClass('/admin/relatorios/etapas')}>
            <Link className="nav-link" to="/admin/relatorios/etapas">
              <i className="fas fa-fw fa-tasks" />
              <span>Relatório de Etapas</span>
            </Link>
          </li>
        )}

        {hasPermissao('relatorio.contratosSemAtendimento') && (
          <li className={navItemClass('/admin/relatorios/contratos-sem-atendimento')}>
            <Link className="nav-link" to="/admin/relatorios/contratos-sem-atendimento">
              <i className="fas fa-fw fa-exclamation-triangle" />
              <span>Contratos sem Atendimento</span>
            </Link>
          </li>
        )}

        {hasPermissao('relatorio.atendimentosDuplicados') && (
          <li className={navItemClass('/admin/relatorios/atendimentos-duplicados')}>
            <Link className="nav-link" to="/admin/relatorios/atendimentos-duplicados">
              <i className="fas fa-fw fa-clone" />
              <span>Atendimentos Duplicados</span>
            </Link>
          </li>
        )}

        {hasPermissao('relatorio.agendaPericiaAvaliacao') && (
          <li className={navItemClass('/admin/relatorios/agenda-pericia-avaliacao')}>
            <Link className="nav-link" to="/admin/relatorios/agenda-pericia-avaliacao">
              <i className="fas fa-fw fa-folder-open" />
              <span>Agenda Perícia/Avaliação</span>
            </Link>
          </li>
        )}

        {hasPermissao('menu.contratosAssinados') && (
          <li className={navItemClass('/relatorio-estrategico')}>
            <Link className="nav-link" to="/relatorio-estrategico">
              <i className="fas fa-fw fa-file-signature" />
              <span>Contratos Assinados</span>
            </Link>
          </li>
        )}
      </GrupoMenu>

      <GrupoMenu titulo="Gestão">
        {hasPermissao('menu.pagamentos') && (
          <li className={navItemClass('/pagamentos')}>
            <Link className="nav-link" to="/pagamentos">
              <i className="fas fa-fw fa-hand-holding-usd" />
              <span>Pagamentos</span>
            </Link>
          </li>
        )}

        {canAccessContratos && (
          <li className={navItemClass('/admin/permissoes')}>
            <Link className="nav-link" to="/admin/permissoes">
              <i className="fas fa-fw fa-user-shield" />
              <span>Permissões por Setor</span>
            </Link>
          </li>
        )}

        {canAccessContratos && (
          <li className={navItemClass('/admin/como-funcionamos')}>
            <Link className="nav-link" to="/admin/como-funcionamos">
              <i className="fas fa-fw fa-building" />
              <span>Como a Empresa Funciona</span>
            </Link>
          </li>
        )}

        {canAccessContratos && (
          <li className={navItemClass('/admin/usuarios-online')}>
            <Link className="nav-link" to="/admin/usuarios-online">
              <i className="fas fa-fw fa-circle text-success" />
              <span>Usuários Online</span>
            </Link>
          </li>
        )}

        {canAccessContratos && (
          <li className={navItemClass('/admin/usuarios-inatividade')}>
            <Link className="nav-link" to="/admin/usuarios-inatividade">
              <i className="fas fa-fw fa-user-clock" />
              <span>Tempo sem Login</span>
            </Link>
          </li>
        )}

        {canAccessConfigMenu && (
          <li className="nav-item">
            <a
              className={`nav-link${openConfiguracoes ? '' : ' collapsed'}`}
              href="#"
              aria-expanded={openConfiguracoes}
              onClick={(e) => { e.preventDefault(); setOpenConfiguracoes((v) => !v); }}
            >
              <i className="fas fa-fw fa-cog" />
              <span>Configurações</span>
            </a>
            <Collapse in={openConfiguracoes}>
              <div>
                <div className="bg-white py-2 collapse-inner rounded">
                  {hasPermissao('menu.usuarios') && (
                    <Link className="collapse-item" to="/users">Usuários</Link>
                  )}

                  {(hasPermissao('configuracoes.lembreteAtendimento')
                    || hasPermissao('configuracoes.lembretePresencial')
                    || hasPermissao('configuracoes.lembretePendencias')) && (
                    <>
                      <a
                        className="collapse-item"
                        href="#"
                        aria-expanded={openLembretes}
                        onClick={(e) => { e.preventDefault(); setOpenLembretes((v) => !v); }}
                      >
                        Lembretes
                      </a>
                      <Collapse in={openLembretes}>
                        <div className="ps-3">
                          {hasPermissao('configuracoes.lembreteAtendimento') && (
                            <Link className="collapse-item" to="/atendimentos-presenciais/lembretes">Lembrete de Atendimento</Link>
                          )}
                          {hasPermissao('configuracoes.lembretePresencial') && (
                            <Link className="collapse-item" to="/atendimentos-presenciais/lembretes-presencial">Lembrete de Reagendamento</Link>
                          )}
                          {hasPermissao('configuracoes.lembretePendencias') && (
                            <Link className="collapse-item" to="/lembrete-pendencias">Lembrete de Pendências</Link>
                          )}
                        </div>
                      </Collapse>
                    </>
                  )}

                  {hasPermissao('configuracoes.origens') && (
                    <Link className="collapse-item" to="/pessoas/origens">Editar Lista de Origens</Link>
                  )}
                  {hasPermissao('configuracoes.indicacoes') && (
                    <Link className="collapse-item" to="/pessoas/indicacoes">Editar Lista de Indicações</Link>
                  )}
                  {hasPermissao('configuracoes.beneficios') && (
                    <Link className="collapse-item" to="/pessoas/beneficios">Editar Lista de Benefícios</Link>
                  )}
                  {hasPermissao('configuracoes.pendencias') && (
                    <Link className="collapse-item" to="/pessoas/pendencias">Editar Lista de Pendências</Link>
                  )}
                  {hasPermissao('configuracoes.advogados') && (
                    <Link className="collapse-item" to="/pessoas/advogados">Editar Lista de Prestadores de Serviços</Link>
                  )}
                  {hasPermissao('configuracoes.unidades') && (
                    <Link className="collapse-item" to="/unidades">Editar Lista de Unidades</Link>
                  )}
                  {hasPermissao('configuracoes.lembretesTipos') && (
                    <Link className="collapse-item" to="/atendimentos-presenciais/lembretes-configuracoes">Editar Lembretes</Link>
                  )}
                  {hasPermissao('configuracoes.vagasAtendimento') && (
                    <Link className="collapse-item" to="/atendimentos-presenciais/vagas-atendimento-configuracoes">Editar Vagas de Atendimento</Link>
                  )}
                  {hasPermissao('configuracoes.painelChamados') && (
                    <Link className="collapse-item" to="/painel-chamados">Painel de Chamados</Link>
                  )}
                </div>
              </div>
            </Collapse>
          </li>
        )}
      </GrupoMenu>

      <GrupoMenu titulo="Suporte">
        <li className={navItemClass('/novidades')}>
          <Link className="nav-link" to="/novidades">
            <i className="fas fa-fw fa-newspaper" />
            <span>Novidades do sistema</span>
          </Link>
        </li>

        {hasPermissao('menu.chamados') && (
          <li className={navItemClass('/chamados')}>
            <Link className="nav-link" to="/chamados">
              <i className="fas fa-fw fa-life-ring" />
              <span>Chamados de TI</span>
            </Link>
          </li>
        )}

        {hasPermissao('menu.ajuda') && (
          <li className={navItemClass('/tutorial-sistema')}>
            <Link className="nav-link" to="/tutorial-sistema">
              <i className="fas fa-fw fa-book" />
              <span>Tutorial do Sistema</span>
            </Link>
          </li>
        )}
      </GrupoMenu>

      {/* Ajuda, Ajuda do Sistema, Perfil e Sair agora vivem só na Navbar. */}
      <hr className="sidebar-divider mt-auto" />

      <div className="text-center d-none d-md-inline">
        <button
          className="rounded-circle border-0"
          id="sidebarToggle"
          type="button"
          onClick={handleToggleSidebar}
          title="Recolher/expandir menu"
          aria-label="Recolher/expandir menu"
        />
      </div>
    </ul>
  );
}
